"use client";

import type { ReactNode } from 'react';
import { ShaderBackground } from './ShaderBackground';

interface FeatureCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  gradientFrom: string;
  gradientTo: string;
}

function FeatureCard({ icon, title, description, gradientFrom, gradientTo }: FeatureCardProps) {
  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300 group">
      <div
        className={`w-12 h-12 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-lg mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
      >
        {icon}
      </div>
      <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
      <p className="text-white/70 text-sm">{description}</p>
    </div>
  );
}

interface CTAButtonProps {
  children: ReactNode;
  onClick?: () => void;
  href?: string;
  variant?: 'primary' | 'secondary';
  className?: string;
}

function CTAButton({ children, onClick, href, variant = 'primary', className = '' }: CTAButtonProps) {
  const baseClasses = "font-semibold rounded-lg px-8 py-4 transition-all duration-200 transform hover:scale-105";

  const variantClasses = variant === 'primary'
    ? "text-white bg-gradient-to-r from-orange-400 to-pink-400 hover:from-violet-600 hover:to-purple-700 shadow-lg shadow-violet-500/25"
    : "px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20";

  const Component = href ? 'a' : 'button';

  return (
    <Component
      className={`${baseClasses} ${variantClasses} ${className}`}
      onClick={onClick}
      href={href}
    >
      {children}
    </Component>
  );
}

interface BadgeProps {
  children: ReactNode;
  status?: 'beta' | 'new' | 'free' | 'custom';
  className?: string;
}

function Badge({ children, status = 'custom', className = '' }: BadgeProps) {
  const statusColors = {
    beta: 'bg-green-400',
    new: 'bg-blue-400',
    free: 'bg-emerald-400',
    custom: 'bg-green-400'
  };

  return (
    <div className={`inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-sm text-white/90 mb-8 ${className}`}>
      <span className={`w-2 h-2 ${statusColors[status]} rounded-full mr-2`}></span>
      {children}
    </div>
  );
}

interface HeroSectionProps {
  badge?: ReactNode;
  title: ReactNode;
  subtitle: string;
  primaryCTA: {
    text: string;
    href?: string;
    onClick?: () => void;
  };
  secondaryCTA?: {
    text: string;
    href?: string;
    onClick?: () => void;
  };
  features: Array<{
    icon: ReactNode;
    title: string;
    description: string;
    gradientFrom: string;
    gradientTo: string;
  }>;
  shaderColor?: [number, number, number];
  className?: string;
}

export function HeroSection({
  badge,
  title,
  subtitle,
  primaryCTA,
  secondaryCTA,
  features,
  shaderColor = [0.8, 0.4, 1],
  className = ''
}: HeroSectionProps) {
  return (
    <section className={`relative min-h-screen overflow-hidden ${className}`}>
      {/* Shader Background */}
      <ShaderBackground
        color={shaderColor}
        speed={0.6}
        amplitude={0.12}
      />

      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/20"></div>

      {/* Hero Content */}
      <div className="relative z-20 flex items-center justify-center min-h-screen px-6">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          {badge && badge}

          {/* Main Heading */}
          <h1 className="md:text-7xl lg:text-8xl leading-tight text-5xl font-bold text-white tracking-tighter mb-6 py-0">
            {title}
          </h1>

          {/* Subheading */}
          <p className="text-xl md:text-2xl text-white/80 mb-12 max-w-3xl mx-auto leading-relaxed">
            {subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <CTAButton
              variant="primary"
              href={primaryCTA.href}
              onClick={primaryCTA.onClick}
            >
              {primaryCTA.text}
            </CTAButton>
            {secondaryCTA && (
              <CTAButton
                variant="secondary"
                href={secondaryCTA.href}
                onClick={secondaryCTA.onClick}
              >
                {secondaryCTA.text}
              </CTAButton>
            )}
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                gradientFrom={feature.gradientFrom}
                gradientTo={feature.gradientTo}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex flex-col items-center text-white/60">
          <span className="text-sm mb-2">Scroll to explore</span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Export individual components for more granular usage
export { FeatureCard, CTAButton, Badge };